import { DateTime } from 'luxon'
import { useCallback, useMemo } from 'react'

import type { ActivationGroupOption } from '@/features/bidding/components/bidDetail/toolbar/ActivationGroupMultiSelect'
import type { MarketProgram } from '@/features/bidding/constants'
import { BidType, MARKET_TIMEZONE } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import {
  useCapacityObligationsQuery,
  useInvalidateCapacityObligationsQuery,
} from '@/features/bidding/hooks/bidDetail/energy/useCapacityObligationsQuery'
import { useEnergyActivationGroupsQuery } from '@/features/bidding/hooks/bidDetail/energy/useEnergyActivationGroupsQuery'
import { useLatestEnergyBidQuery } from '@/features/bidding/hooks/bidDetail/energy/useLatestEnergyBidQuery'
import { useTickEveryMinute } from '@/features/bidding/hooks/bidDetail/useTickEveryMinute'
import type { BidEnergyPtu } from '@/features/bidding/types/bidDetail/BidEnergyPtu'
import { computePerActivationGroupObligationTotals } from '@/features/bidding/utils/bidDetail/computePerActivationGroupObligationTotals'
import { buildBidEnergyPtus } from '@/features/bidding/utils/bidDetail/energy/buildBidEnergyPtus'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import getBidConfig from '@/features/bidding/utils/getBidConfig'

export type UseBidEnergyViewRowsParams = Readonly<{
  marketProgram: MarketProgram
  portfolioCode: string | null
  deliveryDay: MarketDate | null
  selectedActivationGroupUuids: ReadonlyArray<string>
  ptuRange: Readonly<{ fromIndex: number; toIndex: number }>
  hideZeroVolumeRows: boolean
}>

export type UseBidEnergyViewRowsResult = Readonly<{
  ptus: ReadonlyArray<BidEnergyPtu>
  availableActivationGroups: ReadonlyArray<ActivationGroupOption>
  isLoading: boolean
  isFetching: boolean
  isBidError: boolean
  isActivationGroupsError: boolean
  isObligationsError: boolean
  refresh: () => void
}>

/**
 * Composes the three Energy data streams (AG list, latest bid per AG,
 * obligations) into the PTU tree rendered by `BidEnergyStepsTable`.
 *
 * - **AG list** comes from `/activation-group?countryCode=X` filtered to
 *   the active `marketProgram` — the master list, not just AGs with bids
 * - **Latest bid** fires one `/bid` call per selected AG (parallel via
 *   `useQueries` inside `useLatestEnergyBidQuery`)
 * - **Obligations** come from `/capacity-obligations`
 */
export function useBidEnergyViewRows(params: UseBidEnergyViewRowsParams): UseBidEnergyViewRowsResult {
  const { marketProgram, portfolioCode, deliveryDay, selectedActivationGroupUuids, ptuRange, hideZeroVolumeRows } =
    params

  const { selectedCountry } = useBiddingContext()

  // Master AG list — full /activation-group response filtered by market.
  const ags = useEnergyActivationGroupsQuery({
    countryCode: selectedCountry,
    marketProgram,
  })

  // Per-AG bid fetches (parallel), one query per selected AG.
  const latestBid = useLatestEnergyBidQuery({
    marketProgram,
    portfolioCode,
    deliveryDay,
    activationGroupUuids: selectedActivationGroupUuids,
  })

  const obligations = useCapacityObligationsQuery({ marketProgram, portfolioCode, deliveryDay })

  const invalidateBid = latestBid.invalidate
  const invalidateObligations = useInvalidateCapacityObligationsQuery()
  const refresh = useCallback(() => {
    invalidateBid()
    void invalidateObligations()
  }, [invalidateBid, invalidateObligations])

  const availableActivationGroups = useMemo<ActivationGroupOption[]>(() => {
    const obligationByActivationGroup = computePerActivationGroupObligationTotals(obligations.obligations ?? [])

    return ags.activationGroups
      .map((ag) => ({
        uuid: ag.uuid,
        code: ag.code,
        oblWatts: obligationByActivationGroup.get(ag.uuid),
      }))
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))
  }, [ags.activationGroups, obligations.obligations])

  // Re-render every minute (or on focus) so the "NOW" badge + past-PTU
  // detection stay accurate after long idle.
  const tick = useTickEveryMinute()

  const ptus = useMemo<BidEnergyPtu[]>(() => {
    if (!deliveryDay) {
      return []
    }

    return buildBidEnergyPtus({
      snapshots: latestBid.snapshots,
      obligations: obligations.obligations ?? [],
      selectedActivationGroupUuids,
      activationGroupOptions: availableActivationGroups,
      ptuRange,
      ptuLengthInMinutes: getBidConfig(selectedCountry, BidType.ENERGY).ptuLengthInMin,
      deliveryDay,
      now: DateTime.now().setZone(MARKET_TIMEZONE),
      hideZeroVolumeRows,
    })
  }, [
    latestBid.snapshots,
    obligations.obligations,
    selectedActivationGroupUuids,
    availableActivationGroups,
    ptuRange,
    deliveryDay,
    tick,
    selectedCountry,
    hideZeroVolumeRows,
  ])

  return {
    ptus,
    availableActivationGroups,
    isLoading: ags.isLoading || latestBid.isLoading || obligations.isLoading,
    isFetching: latestBid.isFetching || obligations.isFetching,
    isBidError: latestBid.isError,
    isActivationGroupsError: ags.isError,
    isObligationsError: obligations.isError,
    refresh,
  }
}
